import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Check, Loader2 } from "lucide-react";

const STEPS = [
  "Uploading documents",
  "Extracting assignment brief",
  "Reading supporting documents",
  "Identifying assessment type",
  "Evaluating against rubric",
  "Predicting grade",
  "Generating feedback",
  "Preparing report",
  "Complete",
];

export const MyAEProgress = ({ done = false }) => {
  const [step, setStep] = useState(0);
  const last = STEPS.length - 1;

  useEffect(() => {
    if (done) { setStep(last); return; }
    // advance automatically, but hold at the penultimate step until `done`
    const t = setInterval(() => setStep((s) => (s < last - 1 ? s + 1 : s)), 3200);
    return () => clearInterval(t);
  }, [done, last]);

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 py-16" data-testid="myae-progress">
      <div className="relative mb-10">
        <motion.div
          className="w-24 h-24 rounded-3xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-[0_16px_48px_rgba(79,70,229,0.4)]"
          animate={done ? { scale: 1 } : { scale: [1, 1.05, 1] }}
          transition={{ duration: 1.6, repeat: done ? 0 : Infinity, ease: "easeInOut" }}
        >
          {done ? <Check size={40} className="text-white" /> : <FileText size={38} className="text-white" />}
        </motion.div>
        {!done && (
          <motion.span
            className="absolute -inset-2 rounded-[28px] border-2 border-indigo-300/60"
            animate={{ opacity: [0.2, 0.6, 0.2], scale: [1, 1.08, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
      </div>

      <p className="overline text-indigo-500 mb-2">{done ? "Done" : "Analysing your assignment"}</p>
      <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-zinc-900 mb-8 text-center">
        {done ? "Your report is ready" : "This usually takes under a minute"}
      </h2>

      <div className="w-full max-w-md space-y-1">
        {STEPS.map((label, i) => {
          const state = i < step ? "done" : i === step ? "active" : "todo";
          return (
            <AnimatePresence key={label}>
              <motion.div
                initial={false}
                animate={{ opacity: state === "todo" ? 0.35 : 1 }}
                className="flex items-center gap-3 py-2"
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  state === "done" ? "bg-emerald-100 text-emerald-700" : state === "active" ? "bg-indigo-100 text-indigo-700" : "bg-zinc-100 text-zinc-400"
                }`}>
                  {state === "done" ? <Check size={13} /> : state === "active" ? <Loader2 size={13} className="animate-spin" /> : <span className="text-[11px] font-medium">{i + 1}</span>}
                </span>
                <span className={`text-sm ${state === "active" ? "font-medium text-zinc-900" : "text-zinc-600"}`}>{label}</span>
              </motion.div>
            </AnimatePresence>
          );
        })}
      </div>
    </div>
  );
};
